import { MapPin, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

export interface BranchInfo {
  id: string;
  name: string;
  name_ar: string | null;
  address: string | null;
  address_ar: string | null;
  phone: string | null;
  is_open: boolean;
  is_busy: boolean;
  estimated_prep_time: number | null;
  working_hours: string | null;
  latitude: number | null;
  longitude: number | null;
  distance_km?: number | null;
}

interface Props {
  branch: BranchInfo;
  onSelect: (branch: BranchInfo) => void;
}

export default function BranchCard({ branch, onSelect }: Props) {
  const { language, t } = useLanguage();

  const name = language === "ar" && branch.name_ar ? branch.name_ar : branch.name;
  const address = language === "ar" && branch.address_ar ? branch.address_ar : branch.address;

  const statusDot = !branch.is_open
    ? "bg-destructive"
    : branch.is_busy
    ? "bg-warning"
    : "bg-success";

  const statusLabel = !branch.is_open
    ? t("branch.closed")
    : branch.is_busy
    ? t("branch.busy")
    : t("branch.open");

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        !branch.is_open
          ? "border-border bg-muted/50 opacity-70"
          : "border-border bg-card hover:border-primary/50 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${statusDot}`} />
            <h3 className="font-bold text-foreground truncate">{name}</h3>
            {branch.distance_km != null && (
              <span className="text-xs text-muted-foreground shrink-0">
                {t("branch.kmAway").replace("{km}", branch.distance_km.toFixed(1))}
              </span>
            )}
          </div>

          {address && (
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{address}</span>
            </p>
          )}

          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            {branch.is_open && branch.estimated_prep_time && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t("branch.readyIn").replace("{min}", String(branch.estimated_prep_time))}
              </span>
            )}
            {branch.working_hours && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {branch.working_hours}
              </span>
            )}
          </div>

          {branch.is_busy && branch.is_open && (
            <p className="text-xs text-warning mt-1.5">
              ⚠️ {t("branch.branchBusyDesc")}
            </p>
          )}

          {!branch.is_open && (
            <p className="text-xs text-destructive mt-1.5">
              {statusLabel}
            </p>
          )}
        </div>

        <Button
          size="sm"
          disabled={!branch.is_open}
          onClick={() => onSelect(branch)}
          className="shrink-0"
        >
          {branch.is_open ? t("branch.select") : t("branch.closed")}
        </Button>
      </div>
    </div>
  );
}
